<script setup lang="ts">
import {
  useMarkAsAbsentedKollegeUserCourseLessonMutation,
  useMarkAsCompletedKollegeUserCourseLessonMutation,
  type UserCourseLessonCardKollegeUserCourseLessonFragment,
} from './UserCourseLessonCard.generated'
import FlatButton from '@/components/atoms/FlatButton.vue'
import UserCourseLessonStatus from './UserCourseLessonStatus/UserCourseLessonStatus.vue'
import { computed, ref } from 'vue'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { KollegeUserCourseLessonStatus } from '@/types/schema.generated'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  fragment: UserCourseLessonCardKollegeUserCourseLessonFragment
}>()

const startsAtText = computed<string | undefined>(() => {
  if (props.fragment.schedule == null) {
    return undefined
  }
  return format(props.fragment.schedule.startsAt, 'yyyy/MM/dd(E) HH:mm', {
    locale: ja,
  })
})

const alert = useAlert()

//
// 欠席
//

const disabledMarkAsAbsentedButton = computed(() => {
  const canMarkAsAbsented =
    props.fragment.status === KollegeUserCourseLessonStatus.Booked

  return !canMarkAsAbsented
})

const isLoadingMarkAsAbsented = ref(false)

const markAsAbsentedMutation =
  useMarkAsAbsentedKollegeUserCourseLessonMutation()

const handleClickMarkAsAbsentedButton = async () => {
  if (
    !window.confirm(
      `${props.fragment.courseLesson.title} を欠席にしてよろしいですか？`
    )
  ) {
    return
  }

  isLoadingMarkAsAbsented.value = true

  try {
    await markAsAbsentedMutation.mutate({
      input: {
        userCourseLessonID: props.fragment.id,
      },
    })

    alert.success(`${props.fragment.courseLesson.title} を欠席にしました。`)
  } catch (e) {
    console.error(e)
    alert.error('欠席に失敗しました。')
  } finally {
    isLoadingMarkAsAbsented.value = false
  }
}

//
// 受講完了
//

const disabledMarkAsCompletedButton = computed(() => {
  const canMarkAsCompleted =
    props.fragment.status === KollegeUserCourseLessonStatus.Booked

  return !canMarkAsCompleted
})

const isLoadingMarkAsCompleted = ref(false)

const markAsCompletedMutation =
  useMarkAsCompletedKollegeUserCourseLessonMutation()

const handleClickMarkAsCompletedButton = async () => {
  if (
    !window.confirm(
      `${props.fragment.courseLesson.title} を受講完了してよろしいですか？`
    )
  ) {
    return
  }

  isLoadingMarkAsCompleted.value = true

  try {
    await markAsCompletedMutation.mutate({
      input: {
        userCourseLessonID: props.fragment.id,
      },
    })

    alert.success(`${props.fragment.courseLesson.title} を受講完了しました。`)
  } catch (e) {
    console.error(e)
    alert.error('受講完了に失敗しました。')
  } finally {
    isLoadingMarkAsCompleted.value = false
  }
}
</script>

<template>
  <div :class="$style.card">
    <UserCourseLessonStatus :status="fragment.status" />
    <h2 :class="$style.title">{{ fragment.courseLesson.title }}</h2>
    <div>
      <span>講師：</span>
      <span>
        {{ fragment.instructor.name }}
      </span>
    </div>
    <div :class="$style.date">
      <span>レッスン日時：</span>
      <span v-if="startsAtText == null">未定</span>
      <span v-else :class="$style.startsAtText">{{ startsAtText }}</span>
    </div>
    <div :class="$style.buttonGroup">
      <FlatButton
        variant="base"
        :disabled="disabledMarkAsAbsentedButton"
        @click="handleClickMarkAsAbsentedButton"
        >欠席</FlatButton
      >
      <FlatButton
        variant="primary"
        :disabled="disabledMarkAsCompletedButton"
        @click="handleClickMarkAsCompletedButton"
        >受講完了</FlatButton
      >
    </div>
  </div>
</template>

<style lang="scss" module>
.card {
  display: flex;
  padding: 24px;
  border-radius: 12px;
  flex-direction: column;
  gap: 12px;
  box-shadow: $shadow-8;
}

.title {
  font-size: 20px;
  font-weight: bold;
  line-height: 1.8;
}

.date {
  padding: 16px;
  border-radius: 8px;
  border: 1px solid $color-border-low-emphasis;
}

.buttonGroup {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.startsAtText {
  color: $color-text-and-object-accent-primary;
  font-weight: 700;
}
</style>
